// src/components/Modals/CustomizeModal.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useFeedback } from "../../contexts/FeedbackContext";
import SoundControlsInline from "../Controls/SoundControlsInline";
import { useStaff } from "../../contexts/StaffContext";
import SyncPill from "@/components/Sync/SyncPill";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import { Eye, EyeOff } from "lucide-react";
import { useActiveStudioId } from "../../hooks/useActiveStudioId";
import { useAuth } from "../../contexts/AuthContext";
import * as studiosApi from "../../lib/mudoNetwork/studiosApi";
import { supabase } from "../../supabaseClient";
import { useNotifications } from "../../contexts/NotificationsContext";
import { defaultPricingSettings } from "../../contexts/PricingSettingsContext";
import { useStudio } from "../../contexts/StudioContext";
import { useStorage } from "../../contexts/StorageContext";
import useStripePayoutsGuard from "../../hooks/useStripePayoutsGuard";
import { apiFetch } from "../../lib/apiFetch";
import { authedFetch } from "@/lib/authedFetch";
import { finaliseConnect, fetchConnectStatus } from "@/lib/stripeConnectClient";
import { stripeAccountDashboardUrl } from "@/lib/stripeLinks";
import {
  getNominatimHeaders,
  applyNominatimContact,
} from "../../utils/nominatim";
import { saveStudioSettings, loadStudioSettings } from "@/lib/studioSettingsStore";
import { uploadStudioLogo, deleteStudioLogoByUrl } from "@/lib/studioLogoStorage";

const practiceOptions = ["3D Printing", "Slip Casting", "Wheel Throwing", "Other"];

// Helper to convert snake_case to Title Case
const humanize = (s = "") =>
  String(s)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

// Public Map Tags
const publicMapTags = [
  "Workshops",
  "Kiln Hire",
  "Drop-in Studio",
  "Kids Classes",
  "3D Printing",
  "Wheel Throwing",
  "Slip Casting",
  "Memberships",
  "Private Events",
];

// Theme labels
const themes = [
  "Burnt Banksia",
  "Red Earth Clay",
  "Stormy Gum",
  "Basalt Ridge",
  "Midnight Wattle",
  "Blackwood Bark",
];

/**
 * Mudo theme palettes → CSS variables.
 * Tweak colours if you want, but keep the variable names
 * consistent with how the rest of the app styles things.
 */
const THEME_PALETTES = {
  "Burnt Banksia": {
    "--color-primary": "#f97316",
    "--color-primary-soft": "rgba(249,115,22,0.18)",
    "--color-primary-strong": "#ea580c",
    "--color-glass-bg": "rgba(15,10,8,0.85)",
    "--color-glass-border": "rgba(255,255,255,0.18)",
  },
  "Red Earth Clay": {
    "--color-primary": "#b45309",
    "--color-primary-soft": "rgba(180,83,9,0.18)",
    "--color-primary-strong": "#92400e",
    "--color-glass-bg": "rgba(18,10,6,0.9)",
    "--color-glass-border": "rgba(255,255,255,0.2)",
  },
  "Stormy Gum": {
    "--color-primary": "#0f766e",
    "--color-primary-soft": "rgba(15,118,110,0.18)",
    "--color-primary-strong": "#0e615a",
    "--color-glass-bg": "rgba(8,16,16,0.9)",
    "--color-glass-border": "rgba(255,255,255,0.2)",
  },
  "Basalt Ridge": {
    "--color-primary": "#4b5563",
    "--color-primary-soft": "rgba(75,85,99,0.2)",
    "--color-primary-strong": "#374151",
    "--color-glass-bg": "rgba(7,9,12,0.92)",
    "--color-glass-border": "rgba(255,255,255,0.16)",
  },
  "Midnight Wattle": {
    "--color-primary": "#eab308",
    "--color-primary-soft": "rgba(234,179,8,0.18)",
    "--color-primary-strong": "#ca8a04",
    "--color-glass-bg": "rgba(9,9,2,0.9)",
    "--color-glass-border": "rgba(255,255,255,0.22)",
  },
  "Blackwood Bark": {
    "--color-primary": "#0f172a",
    "--color-primary-soft": "rgba(15,23,42,0.3)",
    "--color-primary-strong": "#020617",
    "--color-glass-bg": "rgba(4,6,13,0.94)",
    "--color-glass-border": "rgba(255,255,255,0.18)",
  },
};

// Normalise theme value coming from settings (string / object / empty)
const normalizeTheme = (val) => {
  if (!val) return "Burnt Banksia";
  if (typeof val === "string") {
    // Guard against "[object Object]" from older saves
    if (val.startsWith("[object")) return "Burnt Banksia";
    return themes.includes(val) ? val : "Burnt Banksia";
  }
  if (typeof val === "object") {
    if (val.name && themes.includes(val.name)) return val.name;
    if (val.label && themes.includes(val.label)) return val.label;
  }
  return "Burnt Banksia";
};

const countries = [
  { name: "Australia", code: "AU" },
  { name: "United States", code: "US" },
  { name: "United Kingdom", code: "GB" },
  { name: "Canada", code: "CA" },
  { name: "New Zealand", code: "NZ" },
  { name: "India", code: "IN" },
  { name: "Germany", code: "DE" },
  { name: "South Africa", code: "ZA" },
];

// Country presets now include tax defaults
const defaultPreferencesByCountry = {
  AU: {
    currency: "AUD",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "GST",
    taxRatePct: 10,
    taxModeDefault: "inclusive",
  },
  US: {
    currency: "USD",
    weight: "lb",
    volume: "fl oz",
    size: "in",
    taxName: "Sales Tax",
    taxRatePct: 0,
    taxModeDefault: "exclusive",
  },
  GB: {
    currency: "GBP",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "VAT",
    taxRatePct: 20,
    taxModeDefault: "inclusive",
  },
  CA: {
    currency: "CAD",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "HST/GST",
    taxRatePct: 0,
    taxModeDefault: "exclusive",
  },
  NZ: {
    currency: "NZD",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "GST",
    taxRatePct: 15,
    taxModeDefault: "inclusive",
  },
  IN: {
    currency: "INR",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "GST",
    taxRatePct: 18,
    taxModeDefault: "inclusive",
  },
  DE: {
    currency: "EUR",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "VAT",
    taxRatePct: 19,
    taxModeDefault: "inclusive",
  },
  ZA: {
    currency: "ZAR",
    weight: "kg",
    volume: "ml",
    size: "cm",
    taxName: "VAT",
    taxRatePct: 15,
    taxModeDefault: "inclusive",
  },
};

const nanoid = () =>
  Math.random().toString(36).slice(2) + Date.now().toString(36);

// MapClickHandler component for handling map clicks
function MapClickHandler({ onLocationClick }) {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationClick(lat, lng);
    },
  });
  return null;
}

// ------- helpers -------
const normalizeWebsite = (val) => {
  const v = (val || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};

export default function CustomizeModal({
  onClose,
  settings,
  updateSettings,
  onFinished,
  forceBlocking = false,
  initialTab = null,
  initialShowMapPicker = false,
}) {
  const { play, buzz } = useFeedback();
  const { staffList = [], addStaff, updateStaff } = useStaff();
  const {
    publicProfile,
    isPublished,
    publishStudio,
    unpublishStudio,
    setMapVisibility,
    refreshOwnProfile,
    refreshAllStudios,
    loading: publicLoading,
  } = usePublicStudio() || {};
  const rootRef = useRef(null);
  const { studioId, setStudioId } = useActiveStudioId();
  const { userId, profile } = useAuth() || {};
  const { notifyAndInbox } = useNotifications() || {};
  const [creatingStudio, setCreatingStudio] = useState(false);
  
  // Studio profile persistence via StudioContext
  const { studio, setStudio, updateStudio } = useStudio() || {};
  const { saveJSON } = useStorage() || {};
  const { stripeAccountId, ensureStripeConnected } = useStripePayoutsGuard() || {};

  // Stripe finalise state
  const [finalisingStripe, setFinalisingStripe] = useState(false);
  const [stripeErr, setStripeErr] = useState("");

  // Check if UUID is valid
  const looksLikeUuid = (s) => {
    if (typeof s !== "string") return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s.trim());
  };
  const studioIdValid = useMemo(() => looksLikeUuid(studioId || ""), [studioId]);

  // Ensure a Supabase studio row exists so Stripe status can be persisted.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUserId = sessionData?.session?.user?.id || null;
        if (!sessionUserId) return;
        const preferredName = (settings?.businessName || "").trim() || "My Studio";
        const { data, error } = await studiosApi.ensureStudioRowForUser(sessionUserId, { name: preferredName });
        if (!alive) return;
        if (error) {
          console.warn("[CustomizeModal] ensureStudioRowForUser failed", error);
          return;
        }
        if (data?.id && !studioIdValid) {
          setStudioId?.(data.id);
        }
        if (data?.id && typeof updateStudio === "function") {
          await updateStudio({ id: data.id, studioId: data.id });
        }
      } catch (e) {
        console.warn("[CustomizeModal] ensureStudioRowForUser unexpected", e);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Load customization settings from Supabase when modal opens with valid studioId
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const sid = studioId || studio?.studioId || studio?.id;
        if (!sid || !looksLikeUuid(sid)) return;
        
        const res = await loadStudioSettings(sid);
        if (!alive) return;
        
        if (res.ok && res.settings) {
          console.log("[CustomizeModal] Loaded settings from Supabase", res.settings);
          setDraft((prev) => ({
            ...prev,
            // Core studio info
            businessName: res.settings.businessName || prev.businessName || "",
            logoUrl: res.settings.logoUrl || prev.logoUrl || "",
            website: res.settings.website || prev.website || "",
            email: res.settings.email || prev.email || "",
            country: res.settings.country || prev.country || "Australia",
            region: res.settings.region || prev.region || "",
            location: res.settings.location || prev.location || "",
            bio: res.settings.bio || prev.bio || "",
            practiceTypes: res.settings.practiceTypes || prev.practiceTypes || [],
            currency: res.settings.currency || prev.currency || "AUD",
            weight: res.settings.weight || prev.weight || "g",
            volume: res.settings.volume || prev.volume || "ml",
            size: res.settings.size || prev.size || "mm",
            productionOutput: res.settings.productionOutput || prev.productionOutput || 100,
            // Theme
            theme: res.settings.theme || prev.theme || "Burnt Banksia",
            isDarkMode: res.settings.isDarkMode ?? prev.isDarkMode,
            // User
            userName: res.settings.userName || prev.userName || "",
            userEmail: res.settings.userEmail || prev.userEmail || "",
            // Tax
            taxName: res.settings.taxName ?? prev.taxName ?? "GST",
            taxRatePct: res.settings.taxRatePct ?? prev.taxRatePct ?? 10,
            taxModeDefault: res.settings.taxModeDefault || prev.taxModeDefault || "inclusive",
            // Pricing
            pricingSettings: res.settings.pricingSettings || prev.pricingSettings || {},
          }));
          // Update preview logo so it displays immediately
          if (res.settings.logoUrl) {
            setPreviewLogo(res.settings.logoUrl);
          }
        } else if (res.error) {
          console.warn("[CustomizeModal] loadStudioSettings error", res.error);
        }
      } catch (e) {
        console.warn("[CustomizeModal] loadStudioSettings exception", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studioId, studio?.studioId, studio?.id]);

  // Local DRAFT — all edits happen here, no global writes while typing
  // Prioritize studio (from Supabase via StudioContext) over settings (from vault)
  const [draft, setDraft] = useState(() => {
    const source = studio || settings;
    return {
      // Studio
      businessName: source.businessName || "",
      logoUrl: source.logoUrl || "",
      website: source.website || "",
      email: source.email || "",
      country: source.country || "Australia",
      region: source.region || "",
      location: source.location || "",
      bio: source.bio || "",
      practiceTypes: Array.isArray(source.practiceTypes)
        ? source.practiceTypes
        : [],
      currency: source.currency || "AUD",
      weight: source.weight || "g",
      volume: source.volume || "ml",
      size: source.size || "mm",
      productionOutput: Number(source.productionOutput ?? 100) || 100,
      // Theme
      theme: normalizeTheme(source.theme),
      isDarkMode: !!source.isDarkMode,
      // User
      userName: source.userName || "",
      userEmail: source.userEmail || "",
      // Tax
      taxName: source.taxName ?? "GST",
      taxRatePct:
        typeof source.taxRatePct === "number" ? source.taxRatePct : 10,
      taxModeDefault: source.taxModeDefault || "inclusive",
      // Pricing & AI
      pricingSettings: {
        ...defaultPricingSettings,
        ...(source?.pricingSettings || {}),
      },
    };
  });

  // Public Profile Draft
  const [publicDraft, setPublicDraft] = useState(() => {
    // Parse existing address if available
    const existingAddress = publicProfile?.address || "";
    const addressParts = existingAddress.split(",").map(p => p.trim());
    
    return {
      displayName: publicProfile?.display_name || settings.businessName || "",
      tags: publicProfile?.tags || [],
      website: publicProfile?.website || settings.website || "",
      bookingUrl: publicProfile?.booking_url || "",
      instagram: publicProfile?.instagram || "",
      showOnMap: publicProfile?.is_active_public ?? false,
      // Structured address fields
      streetNumber: "",
      streetName: "",
      area: "",
      city: settings.region || "",
      state: "",
      country: settings.country || "",
      lat: publicProfile?.lat || null,
      lng: publicProfile?.lng || null,
    };
  });

  // Geocoding state
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState("");
  const [finalizeError, setFinalizeError] = useState("");
  const lastGeocodedAddressRef = useRef("");
  
  // Map picker state
  const [showMapPicker, setShowMapPicker] = useState(!!initialShowMapPicker);
  
  // Map publish/unpublish action busy state
  const [mapActionBusy, setMapActionBusy] = useState(false);

  // Handle Stripe onboarding completion
  const onCompleteOnboarding = useCallback(async () => {
    try {
      setStripeErr("");
      setFinalisingStripe(true);

      // 1) Finalise status from Stripe
      const fin = await finaliseConnect();
      console.log("[stripe] finalise ok", fin);

      // 2) Refresh status in UI (so Pending -> Connected)
      const activeStudioId = studioId || studio?.studioId || studio?.id || null;
      if (!activeStudioId || !looksLikeUuid(activeStudioId)) {
        return;
      }
      const status = await fetchConnectStatus(activeStudioId);
      console.log("[stripe] status after finalise", status);

      // Update studio state with the new status
      if (updateStudio && status) {
        const onboardingComplete =
          typeof status?.onboarding_complete === "boolean"
            ? status.onboarding_complete
            : typeof fin?.onboarding_complete === "boolean"
              ? fin.onboarding_complete
              : !!(status?.details_submitted && status?.charges_enabled && status?.payouts_enabled);

        await updateStudio({
          stripe_account_id_resolved: status?.stripe_account_id_resolved || studio?.stripe_account_id_resolved || null,
          stripe_charges_enabled: !!status?.charges_enabled,
          stripe_payouts_enabled: !!status?.payouts_enabled,
          stripe_details_submitted: !!status?.details_submitted,
          stripe_onboarding_complete: onboardingComplete,
          stripe_requirements_due: status?.requirements || null,
        });
      }

      const completed =
        typeof status?.onboarding_complete === "boolean"
          ? status.onboarding_complete
          : typeof fin?.onboarding_complete === "boolean"
            ? fin.onboarding_complete
            : false;

      if (notifyAndInbox) {
        notifyAndInbox({
          notification: {
            id: `stripe-status-${studioId || "studio"}`,
            title: "Stripe",
            message: completed ? "Stripe onboarding completed" : "Stripe status updated",
            level: completed ? "success" : "info",
            source: "system",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/settings" },
          },
        });
      }
    } catch (e) {
      console.error("[stripe] complete onboarding failed", e);
      setStripeErr(e?.message || "Failed to complete onboarding");
      if (notifyAndInbox) {
        notifyAndInbox({
          notification: {
            id: `stripe-status-error-${studioId || "studio"}`,
            title: "Stripe",
            message: e?.message || "Failed to complete onboarding",
            level: "error",
            source: "system",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/settings" },
          },
        });
      }
    } finally {
      setFinalisingStripe(false);
    }
  }, [studioId, studio?.studioId, studio?.id, updateStudio, notifyAndInbox]);

  // Sync public profile data on mount - never again
  // This prevents the effect from resetting user changes when context updates
  useEffect(() => {
    if (!publicProfile) return;
    
    console.log("[CustomizeModal] Initial mount - syncing publicProfile to publicDraft");
    
    const existingAddress = publicProfile.address || "";
    
    setPublicDraft(prev => ({
      ...prev,
      displayName: publicProfile.display_name || prev.displayName,
      tags: publicProfile.tags || prev.tags || [],
      website: publicProfile.website || prev.website,
      bookingUrl: publicProfile.booking_url || prev.bookingUrl,
      instagram: publicProfile.instagram || prev.instagram,
      showOnMap: publicProfile.is_active_public ?? false,
      lat: publicProfile.lat || prev.lat,
      lng: publicProfile.lng || prev.lng,
    }));
    lastGeocodedAddressRef.current = existingAddress.trim();
  }, []); // Empty dependency array - run ONCE on mount only

  // UI-local
  const [activeTab, setActiveTab] = useState(initialTab || "studio");
  const [previewLogo, setPreviewLogo] = useState(() => {
    const source = studio || settings;
    return source.logoUrl || "";
  });
  const [logoError, setLogoError] = useState("");
  const [cityInput, setCityInput] = useState(settings.region || "");
  const [citySuggestions, setCitySuggestions] = useState([]);

  // derived
  const selectedCountry = useMemo(
    () => countries.find((c) => c.name === draft.country),
    [draft.country]
  );

  // Apply theme palette to <html> whenever draft.theme changes
  useEffect(() => {
    const palette =
      THEME_PALETTES[draft.theme] || THEME_PALETTES["Burnt Banksia"];
    const root = document.documentElement;
    Object.entries(palette).forEach(([cssVar, value]) => {
      root.style.setProperty(cssVar, value);
    });

    try {
      document.dispatchEvent(
        new CustomEvent("mudo:themeChanged", {
          detail: { theme: draft.theme, palette },
        })
      );
    } catch {
      // ignore
    }
  }, [draft.theme]);

  // Apply dark class to <html> *from draft* so theme preview is instant
  useEffect(() => {
    document.documentElement.classList.toggle("dark", !!draft.isDarkMode);
  }, [draft.isDarkMode]);

  // Honor incoming intent to open a specific tab/map picker (e.g., from Mudo Map widget)
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (initialShowMapPicker) {
      setActiveTab("public");
      setShowMapPicker(true);
      setPublicDraft((d) => ({ ...d, showOnMap: true }));
    }
  }, [initialShowMapPicker]);

  // Keyboard shortcuts + initial focus
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!forceBlocking) {
          play("tap");
          buzz("light");
          onClose?.();
        } else {
          play("error");
          buzz("error");
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        handleSaveAndClose();
      }
    }
    window.addEventListener("keydown", onKey);
    const first = rootRef.current?.querySelector?.(
      "input,select,button,textarea"
    );
    first?.focus?.();
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, play, buzz]);

  // Listen for custom event to open directly to public tab
  useEffect(() => {
    function handleOpenToPublic(e) {
      if (e.detail?.tab === "public") {
        setActiveTab("public");
        if (e.detail?.mapPicker) {
          setShowMapPicker(true);
          setPublicDraft((d) => ({ ...d, showOnMap: true }));
        }
      }
    }
    document.addEventListener("mudo:open-customize-modal", handleOpenToPublic);
    return () =>
      document.removeEventListener(
        "mudo:open-customize-modal",
        handleOpenToPublic
      );
  }, []);

  // When settings change from outside (Drive receive), rehydrate draft
  useEffect(() => {
    setDraft((d) => ({
      ...d,
      ...settings,
      pricingSettings: {
        ...defaultPricingSettings,
        ...(settings?.pricingSettings || d.pricingSettings || {}),
      },
      theme: normalizeTheme(settings.theme ?? d.theme),
      productionOutput:
        Number(settings.productionOutput ?? d.productionOutput) ||
        d.productionOutput,
      practiceTypes: Array.isArray(settings.practiceTypes)
        ? settings.practiceTypes
        : d.practiceTypes,
    }));
    setPreviewLogo(settings.logoUrl || "");
  }, [settings]);

  // Country change → apply LOCAL defaults (no global write yet)
  useEffect(() => {
    const code = selectedCountry?.code;
    const prefs = defaultPreferencesByCountry[code] || {};
    setDraft((d) => ({
      ...d,
      currency: prefs.currency ?? d.currency,
      weight: prefs.weight ?? d.weight,
      volume: prefs.volume ?? d.volume,
      size: prefs.size ?? d.size,
      taxName: prefs.taxName ?? d.taxName ?? "GST",
      taxRatePct:
        typeof prefs.taxRatePct === "number"
          ? prefs.taxRatePct
          : typeof d.taxRatePct === "number"
          ? d.taxRatePct
          : 10,
      taxModeDefault: prefs.taxModeDefault ?? d.taxModeDefault ?? "inclusive",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.country]);

  // City suggestions via Nominatim (with abort + debounce)
  useEffect(() => {
    let stop = false;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      const code = selectedCountry?.code;
      if (!cityInput || cityInput.length < 2 || !code) {
        setCitySuggestions([]);
        return;
      }
      try {
        const resp = await axios.get(
          "https://nominatim.openstreetmap.org/search",
          {
            params: {
              q: cityInput,
              countrycodes: code.toLowerCase(),
              format: "json",
              addressdetails: 1,
              limit: 8,
            },
            headers: {
              "Accept-Language": "en",
            },
            signal: controller.signal,
          }
        );
        if (stop) return;
        const options = (resp.data || []).map((it) => ({
          label:
            it.display_name ||
            `${it.name ||
              it.address?.city ||
              it.address?.town ||
              it.address?.village}, ${
              it.address?.state || ""
            }`.trim(),
        }));
        setCitySuggestions(options.map((o) => o.label));
      } catch {
        if (!stop) setCitySuggestions([]);
      }
    }, 350);
    return () => {
      stop = true;
      controller.abort();
      clearTimeout(t);
    };
  }, [cityInput, selectedCountry]);

  const handleCitySelect = (city) => {
    setDraft((d) => ({ ...d, region: city }));
    setCityInput(city);
    setCitySuggestions([]);
    play("tap");
    buzz("light");
  };

  // Logo upload (local preview only; commit on Save)
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError("");

    const allowed = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/svg+xml",
    ];
    const maxBytes = 2 * 1024 * 1024;

    if (!allowed.includes(file.type)) {
      setLogoError("Please upload a PNG, JPG/JPEG, WEBP, or SVG file.");
      play("error");
      buzz("error");
      return;
    }
    if (file.size > maxBytes) {
      setLogoError("Logo is larger than 2MB. Please upload a smaller file.");
      play("error");
      buzz("error");
      return;
    }

    const sid = studioId || studio?.studioId || studio?.id || null;
    if (!sid || !looksLikeUuid(sid)) {
      setLogoError("Please create/connect your Studio first (UUID required) before uploading a logo.");
      play("error");
      buzz("error");
      return;
    }

    try {
      // Optional: show instant local preview while uploading
      const localPreview = URL.createObjectURL(file);
      setPreviewLogo(localPreview);

      const res = await uploadStudioLogo({ studioId: sid, file });
      if (!res.ok) {
        console.error("[CustomizeModal] logo upload failed", res.error);
        setLogoError(res?.error?.message || "Failed to upload logo");
        play("error");
        buzz("error");
        return;
      }

      // Store the PUBLIC URL (not base64)
      setPreviewLogo(res.url);
      setDraft((d) => ({ ...d, logoUrl: res.url }));

      play("success");
      buzz("success");
    } catch (err) {
      console.error("[CustomizeModal] logo upload exception", err);
      setLogoError(err?.message || "Failed to upload logo");
      play("error");
      buzz("error");
    } finally {
      // reset input so same file can be reselected
      try { e.target.value = ""; } catch {}
    }
  };
  const removeLogo = async () => {
    const current = draft.logoUrl || previewLogo || "";
    setPreviewLogo("");
    setDraft((d) => ({ ...d, logoUrl: "" }));
    play("tap");
    buzz("light");

    // best-effort delete from storage if it was a public bucket URL
    try {
      if (current && current.includes("/studio-logos/")) {
        await deleteStudioLogoByUrl(current);
      }
    } catch {}
  };

  const togglePracticeType = (type) => {
    setDraft((d) => {
      const list = Array.isArray(d.practiceTypes) ? [...d.practiceTypes] : [];
      const idx = list.indexOf(type);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(type);
      return { ...d, practiceTypes: list };
    });
    play("tap");
    buzz("light");
  };

  // Public Profile handlers
  const togglePublicTag = (tag) => {
    setPublicDraft((d) => {
      const list = Array.isArray(d.tags) ? [...d.tags] : [];
      const idx = list.indexOf(tag);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(tag);
      return { ...d, tags: list };
    });
    play("tap");
    buzz("light");
  };

  // Handle publish to map
  const handlePublishToMap = useCallback(async () => {
    if (mapActionBusy || !setMapVisibility) {
      console.warn("[CustomizeModal] handlePublishToMap: mapActionBusy or setMapVisibility missing");
      return;
    }
    setMapActionBusy(true);
    try {
      console.log("[CustomizeModal] Publishing to map");
      await setMapVisibility(true, { source: "CustomizeModal" });
      console.log("[CustomizeModal] Published successfully");
      notifyAndInbox?.({
        notification: {
          id: "studio-published-map",
          title: "Studio Published",
          message: "Your studio is now visible on the Mudo Map!",
          level: "success",
          source: "system",
          world: "studio",
        },
      });
      play("success");
      buzz("medium");
    } catch (err) {
      console.error("[CustomizeModal] Publish failed:", err);
      notifyAndInbox?.({
        notification: {
          id: "map-publish-error",
          title: "Publish Failed",
          message: err?.message || "Failed to publish to map",
          level: "error",
          source: "system",
          world: "studio",
        },
      });
      buzz("error");
    } finally {
      setMapActionBusy(false);
    }
  }, [mapActionBusy, setMapVisibility, notifyAndInbox, play, buzz]);

  // Handle unpublish from map
  const handleUnpublishFromMap = useCallback(async () => {
    if (mapActionBusy || !setMapVisibility) {
      console.warn("[CustomizeModal] handleUnpublishFromMap: mapActionBusy or setMapVisibility missing");
      return;
    }
    setMapActionBusy(true);
    try {
      console.log("[CustomizeModal] Unpublishing from map");
      await setMapVisibility(false, { source: "CustomizeModal" });
      console.log("[CustomizeModal] Unpublished successfully");
      notifyAndInbox?.({
        notification: {
          id: "studio-unpublished-map",
          title: "Studio Hidden",
          message: "Your studio is hidden from the Mudo Map",
          level: "info",
          source: "system",
          world: "studio",
        },
      });
      play("tap");
      buzz("light");
    } catch (err) {
      console.error("[CustomizeModal] Unpublish failed:", err);
      notifyAndInbox?.({
        notification: {
          id: "map-unpublish-error",
          title: "Unpublish Failed",
          message: err?.message || "Failed to unpublish",
          level: "error",
          source: "system",
          world: "studio",
        },
      });
      buzz("error");
    } finally {
      setMapActionBusy(false);
    }
  }, [mapActionBusy, setMapVisibility, notifyAndInbox, play, buzz]);

  // Simple Nominatim rate limiter: max 1 req/1100ms + single retry on 429/503
  const nominatimLastAtRef = useRef(0);
  const nominatimHeaders = getNominatimHeaders();
  const nominatimFetch = async (url, { retry = true } = {}) => {
    const wait = Math.max(0, 1100 - (Date.now() - nominatimLastAtRef.current));
    if (wait > 0) {
      await new Promise((r) => setTimeout(r, wait));
    }
    nominatimLastAtRef.current = Date.now();
    const resp = await fetch(url, { headers: nominatimHeaders });
    if ((resp.status === 429 || resp.status === 503) && retry) {
      // Backoff once
      await new Promise((r) => setTimeout(r, 1300));
      nominatimLastAtRef.current = Date.now();
      return nominatimFetch(url, { retry: false });
    }
    return resp;
  };

  // Geocode address to get lat/lng
  const handleGeocodeAddress = async (addressToGeocode = null, options = {}) => {
    const { quiet = false } = options;
    
    // Build address from structured fields if not provided
    let address = addressToGeocode;
    if (!address || typeof address !== "string") {
      const parts = [
        publicDraft.streetNumber,
        publicDraft.streetName,
        publicDraft.area,
        publicDraft.city,
        publicDraft.state,
        publicDraft.country
      ].filter(p => p && p.trim()).map(p => p.trim());
      address = parts.join(", ");
    }
    
    address = address.trim();
    if (!address) {
      setGeocodeError("Please enter an address");
      return null;
    }

    setGeocoding(true);
    setGeocodeError("");
    
    try {
      const url = applyNominatimContact(
        new URL("https://nominatim.openstreetmap.org/search")
      );
      url.searchParams.set("q", address);
      url.searchParams.set("format", "json");
      url.searchParams.set("limit", "1");
      
      const resp = await nominatimFetch(url.toString());
      
      // Handle rate limiting
      if (resp.status === 503 || resp.status === 429) {
        setGeocodeError("Service temporarily busy. Please try again in a moment.");
        buzz("error");
        setGeocoding(false);
        return;
      }
      
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      
      const data = await resp.json();
      
      if (data && data[0]) {
        const result = data[0];
        console.log("[CustomizeModal] Geocoding result:", {
          searched: address,
          found: result.display_name,
          lat: result.lat,
          lng: result.lon
        });
        const coords = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
        };
        setPublicDraft((d) => ({
          ...d,
          lat: coords.lat,
          lng: coords.lng,
        }));
        lastGeocodedAddressRef.current = address;
        setGeocodeError("");
        if (!quiet) {
          play("success");
          buzz("success");
        }
        return coords;
      } else {
        setGeocodeError("Location not found. Please check all address fields are filled in correctly.");
        buzz("error");
        return null;
      }
    } catch (err) {
      console.error("Geocoding failed:", err);
      setGeocodeError("Failed to geocode address. Please try again.");
      buzz("error");
      return null;
    } finally {
      setGeocoding(false);
    }
  };

  const handlePublicProfileSave = async () => {
    // If PublicStudioContext isn't wired up, just bail gracefully
    if (!publishStudio && !unpublishStudio) {
      console.warn("[CustomizeModal] PublicStudioContext not available");
      return;
    }

    console.log("[CustomizeModal] Starting handlePublicProfileSave", {
      isPublished,
      streetNumber: publicDraft.streetNumber,
      streetName: publicDraft.streetName,
      area: publicDraft.area,
      city: publicDraft.city,
      state: publicDraft.state,
      country: publicDraft.country,
    });

    let resolvedLat =
      typeof publicDraft.lat === "number" && !Number.isNaN(publicDraft.lat)
        ? publicDraft.lat
        : null;
    let resolvedLng =
      typeof publicDraft.lng === "number" && !Number.isNaN(publicDraft.lng)
        ? publicDraft.lng
        : null;

    if (isPublished) {
      // Check if coordinates are set
      const missingCoords = !resolvedLat || !resolvedLng;

      if (missingCoords) {
        setGeocodeError("Please pick your location on the map before publishing.");
        notifyAndInbox?.({
          notification: {
            id: "studio-location-missing",
            title: "Studio location",
            message: "Please pick your location on the map",
            level: "error",
            source: "system",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/home" },
          },
        });
        setActiveTab("public");
        return;
      }
    }

    const common = {
      // Name (both cases)
      displayName:
        publicDraft.displayName || draft.businessName || "My Studio",
      display_name:
        publicDraft.displayName || draft.businessName || "My Studio",

      // Location - no address, just coordinates
      city: draft.region || "",
      country: draft.country || "Australia",
      address: "", // Empty since we're not collecting address anymore
      lat: resolvedLat,
      lng: resolvedLng,

      // Tags
      tags: publicDraft.tags || [],

      // Links
      website: normalizeWebsite(publicDraft.website || draft.website),
      bookingUrl: normalizeWebsite(publicDraft.bookingUrl),
      booking_url: normalizeWebsite(publicDraft.bookingUrl),

      // Social
      instagram: (publicDraft.instagram || "").replace(/^@/, ""),

      // Contact
      email: draft.email || "",
    };

    console.log("[CustomizeModal] Payload to save:", common);

    try {
      if (isPublished) {
        console.log("[CustomizeModal] Publishing studio (isPublished = true)");
        const result = await publishStudio?.({
          ...common,
          isActivePublic: true,
          is_active_public: true,
        });
        
        if (result) {
          console.log("[CustomizeModal] Studio published successfully");
          notifyAndInbox?.({
            notification: {
              id: "studio-location-saved",
              title: "Studio location",
              message: "Studio is now visible on Mudo Map",
              level: "success",
              source: "system",
              world: "studio",
              action: { kind: "NAVIGATE", to: "/dashboard/home" },
            },
          });
          play("success");
          buzz("success");
        }
      } else {
        console.log("[CustomizeModal] Unpublishing studio (isPublished = false)");
        if (unpublishStudio) {
          await unpublishStudio();
        } else {
          await publishStudio?.({
            ...common,
            isActivePublic: false,
            is_active_public: false,
          });
        }
        
        console.log("[CustomizeModal] Studio hidden from map");
        notifyAndInbox?.({
          notification: {
            id: "studio-location-hidden",
            title: "Studio location",
            message: "Studio hidden from Mudo Map",
            level: "info",
            source: "system",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/home" },
          },
        });
        play("tap");
        buzz("light");
      }
    } catch (err) {
      console.error("[CustomizeModal] Failed to save public profile:", err);
      
      // Show error to user
      const errorMsg = err?.message || "Failed to save map visibility";
      notifyAndInbox?.({
        notification: {
          id: "studio-location-save-error",
          title: "Studio location",
          message: errorMsg,
          level: "error",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/dashboard/home" },
        },
      });
      
      // Re-throw to prevent the modal from closing
      throw err;
    }
  };

  // Upsert owner into StaffContext
  const upsertStaffOwner = (record) => {
    const existing = (staffList || []).find(
      (s) => String(s.id) === String(record.id)
    );
    if (existing) {
      updateStaff({
        ...existing,
        ...record,
        isOwner: true,
        updatedAt: Date.now(),
      });
    } else {
      addStaff({
        ...record,
        isOwner: true,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    }
    try {
      document.dispatchEvent(new CustomEvent("mudo:staffUpdated"));
    } catch {}
  };

  // ---- Commit everything once
  const handleSaveAndClose = async () => {
    const pricing = {
      ...defaultPricingSettings,
      ...(draft.pricingSettings || {}),
    };
    pricing.targetRetailMargin = Math.min(0.99, Math.max(0, Number(pricing.targetRetailMargin) || 0));
    pricing.targetWholesaleMargin = Math.min(0.99, Math.max(0, Number(pricing.targetWholesaleMargin) || 0));
    pricing.minHourlyRate = Math.max(0, Number(pricing.minHourlyRate) || 0);

    const safe = {
      ...draft,
      website: normalizeWebsite(draft.website),
      productionOutput: Math.max(1, Number(draft.productionOutput) || 1),
      taxRatePct: Number.isFinite(Number(draft.taxRatePct))
        ? Number(draft.taxRatePct)
        : 0,
      pricingSettings: pricing,
    };
    setFinalizeError("");
    setCreatingStudio(true);

    if (typeof updateSettings === "function") {
      // one atomic update — App/Vault will debounce the actual persistence
      updateSettings((prev) => ({ ...prev, ...safe }));
    }
    
    // CRITICAL: Save studio branding/customization to studio/studioProfile.json
    // so it persists in the vault and survives Send/Receive
    try {
      const studioProfile = {
        studioId: looksLikeUuid(studioId || studio?.studioId || studio?.id)
          ? (studioId || studio?.studioId || studio?.id)
          : null,
        displayName: safe.businessName || "My Studio",
        logoUrl: safe.logoUrl || null,
        website: safe.website || "",
        country: safe.country || "Australia",
        region: safe.region || "",
        location: safe.location || "",
        bio: safe.bio || "",
        practiceTypes: safe.practiceTypes || [],
        theme: safe.theme || "Burnt Banksia",
        currency: safe.currency || "AUD",
        weight: safe.weight || "g",
        volume: safe.volume || "ml",
        size: safe.size || "mm",
        productionOutput: safe.productionOutput,
        updatedAt: new Date().toISOString(),
      };
      
      // Update StudioContext (which auto-saves via StorageContext)
      if (setStudio && typeof setStudio === "function") {
        setStudio((prev) => ({ ...prev, ...studioProfile }));
      }
      
      // Also directly save to storage for immediate persistence
      if (saveJSON && typeof saveJSON === "function") {
        await saveJSON("studio/studioProfile.json", studioProfile);
      }
      
      console.log("[CustomizeModal] Saved studio profile to vault", studioProfile);
      // Optional user guidance: suggest sending to Drive
      try {
        notifyAndInbox?.({
          notification: {
            id: `studio-updated-${studioId || "studio"}`,
            title: "Studio",
            message: "Studio updated. Tip: Hit Send to sync to Drive.",
            level: "info",
            source: "system",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/settings/data" },
          },
        });
      } catch {}
    } catch (err) {
      console.warn("[CustomizeModal] Failed to save studio profile:", err);
      // Don't block the save flow - this is best-effort
    }

    // Broadcast tax settings (now that they are committed)
    try {
      document.dispatchEvent(
        new CustomEvent("mudo:taxSettingsChanged", {
          detail: {
            taxName: safe.taxName,
            taxRatePct: safe.taxRatePct,
            taxModeDefault: safe.taxModeDefault,
          },
        })
      );
    } catch {}

    // Ensure owner exists in Staff
    const userId =
      localStorage.getItem("mudoUserId") ||
      (() => {
        const id = nanoid();
        localStorage.setItem("mudoUserId", id);
        return id;
      })();

    upsertStaffOwner({
      id: userId,
      name: (safe.userName || "Owner").trim(),
      email: (safe.userEmail || "").trim(),
      role: "Owner",
      active: true,
      studio: safe.businessName || "My Studio",
      hourlyRate: 0,
      hoursPerWeek: 0,
    });

    // Persist Customize Studio to Supabase so it loads instantly on login
    try {
      const sid = studioId || studio?.studioId || studio?.id || null;
      if (sid && looksLikeUuid(sid)) {
        const res = await saveStudioSettings(sid, safe);
        if (!res.ok) console.warn("[CustomizeModal] saveStudioSettings failed", res.error);
      }
    } catch (e) {
      console.warn("[CustomizeModal] saveStudioSettings exception", e);
    }

    // Always sync public profile visibility when saving, if the context is available
    if (publishStudio || unpublishStudio) {
      try {
        await handlePublicProfileSave();
      } catch (err) {
        console.error("Failed to save public profile:", err);
      }
    }

    if (onFinished) {
      try {
        await onFinished(safe);
      } catch (err) {
        console.error("[CustomizeModal] onFinished failed", err);
        setFinalizeError(err?.message || "Could not finish setup yet.");
        setCreatingStudio(false);
        return;
      }
    }

    play("success");
    buzz("success");
    setCreatingStudio(false);
    onClose?.();
  };

  const handleCancel = () => {
    if (forceBlocking) {
      play("error");
      buzz("error");
      return;
    }
    play("tap");
    buzz("light");
    onClose?.();
  };

  // ---- UI bits
  const TabButton = ({ id, children }) => (
    <button
      type="button"
      onClick={() => {
        setActiveTab(id);
        play("tap");
        buzz("light");
      }}
      className={`px-3 py-1.5 rounded-full text-sm border transition
        ${
          activeTab === id
            ? "bg-[var(--color-primary)]/90 border-[var(--color-primary)] text-white shadow"
            : "bg-black/30 border-white/20 text-white/80 hover:bg-white/10"
        }`}
    >
      {children}
    </button>
  );
  const SectionTitle = ({ children }) => (
    <h3 className="text-sm font-semibold tracking-wide uppercase text-white/80 mb-2">
      {children}
    </h3>
  );
  const inputCls =
    "w-full px-3 py-2 rounded bg-black/30 border border-white/20 text-white";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customize-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          play("tap");
          buzz("light");
          onClose?.();
        }
      }}
    >
      <div
        ref={rootRef}
        className="relative bg-white/10 dark:bg-black/40 text-white rounded-xl p-6 w-full max-w-3xl mx-4 shadow-lg border border-white/20 overflow-auto max-h-[90vh]"
      >
        {/* Header with SyncPill */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="customize-title" className="text-2xl font-bold">
            Mold Your Mud Studio
          </h2>
          <SyncPill />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 justify-center mb-5">
          <TabButton id="studio">Build Studio</TabButton>
          <TabButton id="profile">User Profile</TabButton>
          <TabButton id="public">Public Profile</TabButton>
          <TabButton id="theme">Theme</TabButton>
        </div>

        {/* Payouts & Stripe Connect */}
        {activeTab === "studio" && (
          <div className="space-y-6">
            <div className="p-4 rounded-lg border border-white/20 bg-black/30">
              <StripeStatusSync studio={studio} updateStudio={updateStudio} />
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Payouts & Stripe</h3>
                {(() => {
                  const statusRaw = (studio?.stripe_account_status || (stripeAccountId ? "pending" : "not_connected"));
                  const chargesEnabled = !!studio?.stripe_charges_enabled;
                  const payoutsEnabled = !!studio?.stripe_payouts_enabled;
                  const detailsSubmitted = !!studio?.stripe_details_submitted;
                  const onboardingComplete =
                    typeof studio?.stripe_onboarding_complete === "boolean"
                      ? studio.stripe_onboarding_complete
                      : chargesEnabled && payoutsEnabled && detailsSubmitted;
                  const reqs = studio?.stripe_requirements_due;
                  const currentlyDue = Array.isArray(reqs?.currently_due) ? reqs.currently_due.length : 0;
                  let label = "Pending setup";
                  let cls = "bg-amber-500/20 text-amber-200 border-amber-500/30";
                  if (!stripeAccountId) {
                    label = "Not connected";
                    cls = "bg-red-500/20 text-red-300 border-red-500/30";
                  } else if (onboardingComplete) {
                    label = "Active";
                    cls = "bg-green-500/20 text-green-300 border-green-500/30";
                  } else if (String(statusRaw).toLowerCase() === "restricted" || currentlyDue > 0) {
                    label = "Action required";
                    cls = "bg-red-500/20 text-red-300 border-red-500/30";
                  }
                  return (
                    <span className={`text-xs px-2 py-1 rounded border ${cls}`}>{label}</span>
                  );
                })()}
              </div>
              {(() => {
                const statusRaw = String(studio?.stripe_account_status || "").toLowerCase();
                const connected =
                  !!stripeAccountId &&
                  (typeof studio?.stripe_onboarding_complete === "boolean"
                    ? studio.stripe_onboarding_complete
                    : !!(studio?.stripe_details_submitted && studio?.stripe_charges_enabled && studio?.stripe_payouts_enabled));
                const needsSetup = !!stripeAccountId && !connected;
                if (!stripeAccountId) {
                  return (
                    <>
                      <p className="text-xs text-white/70 mb-3">
                        Connect Stripe to receive payments from visitors. Without this, you can’t receive money for offers, workshops, or studio space bookings.
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          const activeStudioId = studioId || studio?.studioId || studio?.id || null;
                          const res = await ensureStripeConnected({ studioId: activeStudioId });
                          if (!res?.ok && res?.error) {
                            notifyAndInbox?.({
                              notification: {
                                id: `stripe-connect-error-${activeStudioId || "studio"}`,
                                title: "Stripe",
                                message: res.error,
                                level: "error",
                                source: "system",
                                world: "studio",
                                action: { kind: "NAVIGATE", to: "/dashboard/settings" },
                              },
                            });
                          }
                        }}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm"
                      >
                        Connect Stripe
                      </button>
                    </>
                  );
                }
                if (needsSetup) {
                  const reqs = studio?.stripe_requirements_due || {};
                  const currentlyDue = Array.isArray(reqs.currently_due) ? reqs.currently_due : [];
                  const eventuallyDue = Array.isArray(reqs.eventually_due) ? reqs.eventually_due : [];
                  const disabledReason = reqs.disabled_reason || "";

                  return (
                    <>
                      <div className="mt-2 rounded-xl border border-yellow-400/30 bg-yellow-500/10 p-3">
                        <div className="text-sm font-semibold text-yellow-200">Pending setup</div>
                        
                        {/* Display the accountId being onboarded */}
                        {stripeAccountId && (
                          <div className="mt-1 text-xs text-yellow-100/60 font-mono">
                            Account: {stripeAccountId}
                          </div>
                        )}

                        {disabledReason ? (
                          <div className="mt-1 text-xs text-yellow-100/80">
                            <span className="font-semibold">Reason:</span> {humanize(disabledReason)}
                          </div>
                        ) : null}

                        {currentlyDue.length ? (
                          <div className="mt-2 text-xs text-yellow-100/80">
                            <div className="font-semibold text-yellow-100">Stripe needs:</div>
                            <ul className="mt-1 list-disc pl-5 space-y-1">
                              {currentlyDue.map((x) => (
                                <li key={x}>{humanize(x)}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {(!currentlyDue.length && eventuallyDue.length) ? (
                          <div className="mt-2 text-xs text-yellow-100/80">
                            <div className="font-semibold text-yellow-100">May be needed later:</div>
                            <ul className="mt-1 list-disc pl-5 space-y-1">
                              {eventuallyDue.slice(0, 6).map((x) => (
                                <li key={x}>{humanize(x)}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {!currentlyDue.length && !eventuallyDue.length && !disabledReason ? (
                          <div className="mt-2 text-xs text-yellow-100/80">
                            Stripe still reports onboarding incomplete. Click <span className="font-semibold">Continue setup</span>, finish all steps,
                            then click <span className="font-semibold">Complete onboarding</span>.
                          </div>
                        ) : null}
                      </div>

                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              // ✅ ALWAYS reuse stored accountId - never create a new account
                              console.log("[stripe] Continue setup - using accountId:", stripeAccountId);
                              
                              const res = await authedFetch("/api/stripe/connect-onboard", {
                                method: "POST",
                                body: JSON.stringify({ 
                                  accountId: stripeAccountId, // Force reuse of existing account
                                  studioId: studioId || studio?.studioId || studio?.id || null 
                                }),
                              });
                              const json = await res.json().catch(() => ({}));
                              
                              if (res.ok && json?.url) {
                                console.log("[stripe] Onboarding link created for accountId:", json.accountId);
                                window.location.href = json.url;
                              } else {
                                const errorMsg = json?.error || "Failed to create onboarding link";
                                console.error("[stripe] Continue setup failed:", errorMsg);
                                if (notifyAndInbox) {
                                  notifyAndInbox({
                                    notification: {
                                      id: `stripe-continue-error-${stripeAccountId || "account"}`,
                                      title: "Stripe",
                                      message: errorMsg,
                                      level: "error",
                                      source: "system",
                                      world: "studio",
                                      action: { kind: "NAVIGATE", to: "/dashboard/settings" },
                                    },
                                  });
                                }
                              }
                            } catch (e) {
                              console.error("[stripe] Continue setup exception:", e);
                              if (notifyAndInbox) {
                                notifyAndInbox({
                                  notification: {
                                    id: `stripe-continue-exception-${stripeAccountId || "account"}`,
                                    title: "Stripe",
                                    message: e?.message || "Failed to start onboarding",
                                    level: "error",
                                    source: "system",
                                    world: "studio",
                                    action: { kind: "NAVIGATE", to: "/dashboard/settings" },
                                  },
                                });
                              }
                            }
                          }}
                          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm"
                        >
                          Continue setup
                        </button>
                        <button
                          type="button"
                          onClick={onCompleteOnboarding}
                          disabled={finalisingStripe}
                          className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {finalisingStripe ? "Finalising…" : "Complete onboarding"}
                        </button>
                      </div>
                      {stripeErr ? (
                        <div className="mt-2 p-3 rounded bg-red-900/30 border border-red-500/40">
                          <p className="text-sm text-red-300 font-semibold">Error:</p>
                          <p className="text-sm text-red-200">{stripeErr}</p>
                        </div>
                      ) : null}
                    </>
                  );
                }
                return (
                  <>
                    <div className="mt-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
                      <div className="text-sm font-semibold text-emerald-200">Stripe Connected ✅</div>
                      <div className="mt-1 text-xs text-emerald-100/80">
                        Payouts are enabled. You can take bookings and marketplace payments.
                      </div>
                    </div>

                    {/* Admin deep-link to Stripe Dashboard */}
                    {(() => {
                      const dashUrl = stripeAccountDashboardUrl(stripeAccountId);
                      if (!dashUrl) return null;
                      return (
                        <a
                          href={dashUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-3 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/80 hover:bg-white/10"
                        >
                          Open in Stripe Dashboard ↗
                        </a>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* CONTENT */}
        {activeTab === "studio" && (
          <div className="space-y-6">
            {/* ========== STUDIO UUID SYNC STATUS ========== */}
            <div className={`p-4 rounded-lg border ${studioId && studioIdValid ? 'bg-green-900/20 border-green-500/40' : 'bg-orange-900/20 border-orange-500/40'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${studioId && studioIdValid ? 'bg-green-500 shadow-lg shadow-green-500/50' : 'bg-orange-500 shadow-lg shadow-orange-500/50'}`} />
                  <h3 className="font-semibold text-sm">
                    {studioId && studioIdValid ? 'Studio Connected' : 'Studio Setup Required'}
                  </h3>
                </div>
                {studioId && studioIdValid && (
                  <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded border border-green-500/30">
                    ✓ Active
                  </span>
                )}
              </div>

              {studioId && studioIdValid ? (
                <div className="space-y-2">
                  <p className="text-xs text-white/70 mb-2">
                    Your studio is synced to Mudo Network. You can publish products, workshops, and bookable spaces.
                  </p>
                  <div className="bg-black/30 rounded p-2 border border-white/10">
                    <div className="text-[10px] text-white/50 mb-1">Studio UUID:</div>
                    <code className="text-xs text-green-400 font-mono break-all">{studioId}</code>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <div className="flex items-center gap-1.5 text-xs text-white/60">
                      <span className="text-green-400">●</span> Marketplace
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-white/60">
                      <span className="text-green-400">●</span> Workshops
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-white/60">
                      <span className="text-green-400">●</span> Studio Spaces
                    </div>
                  </div>
                </div>
                ) : (
                <div className="space-y-3">
                  <p className="text-xs text-white/70">
                    Connect your studio to Mudo Network to enable marketplace listings, workshops, and bookable spaces.
                  </p>
                  {/* TODO (Copilot): Add a "Payouts & Stripe" panel to Customize Studio.
                      Requirements:
                      1) Read studioProfile.stripe_account_id and maybe studioProfile.stripe_onboarding_complete.
                      2) Render a card titled "Payouts & Stripe Connect" with:
                           - A status pill:
                               * "Not connected" if no stripe_account_id.
                               * "Connected" if stripe_account_id exists.
                           - A paragraph explaining:
                               "Connect Stripe to receive 95% payouts from Mudo Shop sales (Mudo keeps 5%)."
                      3) Add a button:
                           - If not connected: label "Connect Stripe for payouts".
                           - If connected: label "Update payout details".
                      4) On click:
                           - POST to "/api/stripe/create-onboarding-link" with { studioId, userId }.
                           - Read JSON { ok, url }.
                           - If ok && url, set window.location.href = url to send the user to Stripe onboarding.
                           - On error, show a notification (use notify() or alert()).
                  */} 
                  <button
                    type="button"
                    disabled={creatingStudio}
                    onClick={async () => {
                      setCreatingStudio(true);
                      try {
                        // Check auth
                        const { data: u } = await supabase?.auth.getUser() || { data: null };
                        if (!u?.user?.id) {
                          const email = prompt("Enter your email to sign in (we'll send a magic link):", profile?.email || "");
                          if (!email) {
                            setCreatingStudio(false);
                            return;
                          }
                          await supabase?.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin + "/dashboard" } });
                          notifyAndInbox?.({
                            notification: {
                              id: "studio-signin-required",
                              title: "Sign in",
                              message: "Check your email for the magic link, then come back and click Create Studio again.",
                              level: "info",
                              source: "system",
                              world: "studio",
                              action: { kind: "NAVIGATE", to: "/dashboard/home" },
                            },
                          });
                          setCreatingStudio(false);
                          return;
                        }
                        
                        // Create studio
                        const name = draft.businessName || profile?.name || "My Studio";
                        const { data, error } = await studiosApi.createStudioForUser({ name, isPublic: false });
                        if (error || !data?.id) throw error || new Error("Failed to create studio");
                        
                        setStudioId(data.id);
                        notifyAndInbox?.({
                          notification: {
                            id: `studio-created-${data.id}`,
                            title: "Studio",
                            message: `Studio "${data.name}" created and synced!`,
                            level: "success",
                            source: "system",
                            world: "studio",
                            entity: { type: "studio", id: data.id },
                            action: { kind: "NAVIGATE", to: "/dashboard/home" },
                          },
                        });
                        play("success");
                        buzz("medium");
                      } catch (err) {
                        console.error(err);
                        const errMsg = err?.message || String(err);
                        
                        // Check if it's a migration/schema error
                        if (errMsg.includes("could not find") || errMsg.includes("schema cache") || errMsg.includes("relation") || err?.code === 'PGRST204') {
                          notifyAndInbox?.({ 
                            notification: {
                              id: "studio-db-migration-required",
                              title: "Database Setup Required",
                              message: "Please run the Supabase migrations first. See RUN_MIGRATIONS.md in the project root for instructions.",
                              level: "error",
                              source: "system",
                              world: "studio",
                              action: { kind: "NAVIGATE", to: "/dashboard/home" },
                            },
                          });
                        } else {
                          notifyAndInbox?.({
                            notification: {
                              id: `studio-create-error-${studioId || "studio"}`,
                              title: "Studio",
                              message: `Couldn't create studio: ${errMsg}`,
                              level: "error",
                              source: "system",
                              world: "studio",
                              action: { kind: "NAVIGATE", to: "/dashboard/home" },
                            },
                          });
                        }
                      } finally {
                        setCreatingStudio(false);
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingStudio ? 'Creating Studio...' : '⚡ Create Studio & Connect'}
                  </button>
                  <p className="text-[10px] text-white/50 text-center">
                    One-time setup • Secure UUID linked to your account
                  </p>
                </div>
              )}
            </div>

            {/* Logo */}
            <div>
              <SectionTitle>Studio Logo</SectionTitle>
              <label className="block mb-1 text-sm">Add Your Logo</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleLogoUpload}
                className="mb-2 text-sm w-full"
              />
              <p className="text-xs text-orange-300 mb-3">
                Tip: Transparent PNG (square or wide) looks best.
              </p>
              {logoError && (
                <p className="text-xs text-red-300 mb-2">{logoError}</p>
              )}
              {previewLogo ? (
                <div className="flex items-center justify-between mb-2">
                  <img
                    src={previewLogo}
                    alt="Logo Preview"
                    className="h-16 object-contain rounded"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="px-3 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-500"
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>

            {/* Name + Website */}
            <div>
              <SectionTitle>Studio Basics</SectionTitle>

              <label className="block mb-1 text-sm">Name Your Studio</label>
              <input
                type="text"
                value={draft.businessName}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, businessName: e.target.value }))
                }
                className={`${inputCls} mb-3`}
              />

              <label className="block mb-1 text-sm">Studio Website</label>
              <input
                type="url"
                placeholder="https://yourstudio.com"
                value={draft.website}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, website: e.target.value }))
                }
                className={`${inputCls} mb-3`}
              />
              <p className="text-[11px] text-white/60 -mt-2">
                You can paste a bare domain (e.g. <em>yourstudio.com</em>) — we
                ’ll normalize it on save.
              </p>

              <label className="block mb-1 text-sm mt-4">Studio Email</label>
              <input
                type="email"
                placeholder="studio@yourstudio.com"
                value={draft.email}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, email: e.target.value }))
                }
                className={`${inputCls} mb-3`}
              />
              <p className="text-[11px] text-white/60 -mt-2">
                Used for booking notifications and customer inquiries.
              </p>
            </div>

            {/* Country / City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm">Country</label>
                <select
                  value={draft.country}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, country: e.target.value }));
                    play("tap");
                    buzz("light");
                  }}
                  className={inputCls}
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm">City / Region</label>
                <input
                  type="text"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  placeholder="Start typing your city…"
                  className={inputCls}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && citySuggestions.length)
                      handleCitySelect(citySuggestions[0]);
                  }}
                />
                {citySuggestions.length > 0 && (
                  <ul className="bg-black/70 border border-white/20 rounded mt-1 max-h-40 overflow-y-auto text-sm">
                    {citySuggestions.map((name, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleCitySelect(name)}
                        className="px-3 py-1 hover:bg-orange-500 cursor-pointer"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Practice Types */}
            <div>
              <label className="block mb-1 text-sm">Your Practice</label>
              <div className="flex flex-wrap gap-2">
                {practiceOptions.map((type) => (
                  <button
                    type="button"
                    key={type}
                    onClick={() => togglePracticeType(type)}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      (draft.practiceTypes || []).includes(type)
                        ? "bg-orange-500 border-orange-500 text-white"
                        : "bg-black/30 border-white/20 text-white"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block mb-1 text-sm">Short Bio</label>
              <textarea
                rows={2}
                value={draft.bio}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, bio: e.target.value }))
                }
                className={inputCls}
              />
            </div>

            {/* Units */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {["currency", "weight", "volume", "size"].map((key) => (
                <div key={key}>
                  <label className="block mb-1 capitalize text-white/70">
                    {key}
                  </label>
                  <select
                    value={draft[key]}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [key]: e.target.value }))
                    }
                    className={inputCls}
                  >
                    {key === "currency" &&
                      [
                        "AUD",
                        "USD",
                        "EUR",
                        "GBP",
                        "CAD",
                        "NZD",
                        "INR",
                        "ZAR",
                      ].map((opt) => <option key={opt}>{opt}</option>)}
                    {key === "weight" &&
                      ["g", "kg", "lb"].map((opt) => (
                        <option key={opt}>{opt}</option>
                      ))}
                    {key === "volume" &&
                      ["ml", "l", "fl oz"].map((opt) => (
                        <option key={opt}>{opt}</option>
                      ))}
                    {key === "size" &&
                      ["mm", "cm", "in"].map((opt) => (
                        <option key={opt}>{opt}</option>
                      ))}
                  </select>
                </div>
              ))}

              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <label className="block mb-1 text-white/70">
                    Expected Output (units / month)
                  </label>
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                    Important
                  </span>
                </div>
                <input
                  type="number"
                  min={1}
                  value={draft.productionOutput}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      productionOutput: Math.max(
                        1,
                        Number(e.target.value) || 1
                      ),
                    }))
                  }
                  className={inputCls}
                />
                <p className="mt-1 text-xs text-orange-300">
                  Powers your Utilities widget’s per-unit cost and capacity
                  planning.
                </p>
              </div>
            </div>

            {/* Tax & Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
              <div>
                <SectionTitle>Tax & Pricing</SectionTitle>
                <label className="block mb-1 text-sm">Tax Name</label>
                <input
                  type="text"
                  value={draft.taxName}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, taxName: e.target.value }));
                    play("tap");
                    buzz("light");
                  }}
                  className={inputCls}
                  placeholder="GST / VAT / Sales Tax"
                />
              </div>
              <div className="pt-7">
                <label className="block mb-1 text-sm">Tax Rate (%)</label>
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={draft.taxRatePct}
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      taxRatePct: e.target.value,
                    }));
                    play("tap");
                    buzz("light");
                  }}
                  className={inputCls}
                />
              </div>
              <div className="pt-7">
                <label className="block mb-1 text-sm">
                  Default Tax Mode
                </label>
                <select
                  value={draft.taxModeDefault}
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      taxModeDefault: e.target.value,
                    }));
                    play("tap");
                    buzz("light");
                  }}
                  className={inputCls}
                >
                  <option value="inclusive">Inclusive</option>
                  <option value="exclusive">Exclusive</option>
                  <option value="none">No tax</option>
                </select>
              </div>
              <div className="md:col-span-3 -mt-2 text-[11px] text-white/60">
                These defaults prefill calculators (e.g., Packaging, Materials).
                You can still override per item.
              </div>
            </div>

            {/* AI & pricing */}
            <div className="mudo-card p-4 space-y-4">
              <div>
                <h2 className="mudo-section-title">AI &amp; pricing</h2>
                <p className="text-xs opacity-70">
                  Tell Mudo what “good pricing” looks like for your studio. These values are used by the pricing,
                  offer and schedule coaches.
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-3 text-xs">
                <div>
                  <label className="block mb-1 opacity-80">Target retail margin (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={Math.round((draft.pricingSettings?.targetRetailMargin ?? 0) * 100)}
                    onChange={(e) => {
                      const v = Number(e.target.value) || 0;
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: {
                          ...(d.pricingSettings || {}),
                          targetRetailMargin: Math.min(0.99, Math.max(0, v / 100)),
                        },
                      }));
                    }}
                    className="w-full rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  />
                </div>

                <div>
                  <label className="block mb-1 opacity-80">Target wholesale margin (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={Math.round((draft.pricingSettings?.targetWholesaleMargin ?? 0) * 100)}
                    onChange={(e) => {
                      const v = Number(e.target.value) || 0;
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: {
                          ...(d.pricingSettings || {}),
                          targetWholesaleMargin: Math.min(0.99, Math.max(0, v / 100)),
                        },
                      }));
                    }}
                    className="w-full rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  />
                </div>

                <div>
                  <label className="block mb-1 opacity-80">Minimum studio hourly rate</label>
                  <div className="flex items-center gap-1">
                    <span className="opacity-70">$</span>
                    <input
                      type="number"
                      min={0}
                      value={draft.pricingSettings?.minHourlyRate ?? 0}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setDraft((d) => ({
                          ...d,
                          pricingSettings: {
                            ...(d.pricingSettings || {}),
                            minHourlyRate: Math.max(0, v),
                          },
                        }));
                      }}
                      className="w-full rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-400"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-2 text-xs pt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.pricingSettings?.enablePricingAi ?? true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: { ...(d.pricingSettings || {}), enablePricingAi: e.target.checked },
                      }))
                    }
                  />
                  <span className="opacity-80">Enable pricing coach AI</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.pricingSettings?.enableOfferAi ?? true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: { ...(d.pricingSettings || {}), enableOfferAi: e.target.checked },
                      }))
                    }
                  />
                  <span className="opacity-80">Enable offer coach AI</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.pricingSettings?.enableKilnAi ?? true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: { ...(d.pricingSettings || {}), enableKilnAi: e.target.checked },
                      }))
                    }
                  />
                  <span className="opacity-80">Enable kiln coach AI</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.pricingSettings?.enableScheduleAi ?? true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: { ...(d.pricingSettings || {}), enableScheduleAi: e.target.checked },
                      }))
                    }
                  />
                  <span className="opacity-80">Enable schedule coach AI</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.pricingSettings?.enableHelpAi ?? true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricingSettings: { ...(d.pricingSettings || {}), enableHelpAi: e.target.checked },
                      }))
                    }
                  />
                  <span className="opacity-80">Enable help AI</span>
                </label>
              </div>

              <p className="text-[11px] opacity-60">
                These settings only affect suggestions and explanations — Mudo will never change your prices or accept
                offers automatically.
              </p>
            </div>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="space-y-5">
            <SectionTitle>User Profile</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm">Your Name</label>
                <input
                  type="text"
                  value={draft.userName}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, userName: e.target.value }))
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">Email</label>
                <input
                  type="email"
                  value={draft.userEmail}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, userEmail: e.target.value }))
                  }
                  placeholder="name@example.com"
                  className={inputCls}
                />
              </div>
            </div>
            <p className="text-xs text-white/70">
              Saving will automatically create/update your Staff profile for
              this studio.
            </p>
          </div>
        )}

        {activeTab === "theme" && (
          <div className="space-y-6">
            <div>
              <SectionTitle>Theme</SectionTitle>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {themes.map((th) => (
                  <button
                    type="button"
                    key={th}
                    onClick={() => {
                      setDraft((d) => ({ ...d, theme: th }));
                      play("tap");
                      buzz("light");
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                      draft.theme === th
                        ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)] shadow"
                        : "bg-black/30 border-white/20 text-white/70"
                    }`}
                  >
                    {th}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <SectionTitle>Appearance</SectionTitle>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/70">Light</span>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!draft.isDarkMode}
                    onChange={() =>
                      setDraft((d) => ({
                        ...d,
                        isDarkMode: !d.isDarkMode,
                      }))
                    }
                  />
                  <div className="w-11 h-6 bg-white/20 border border-white/30 rounded-full relative">
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform ${
                        draft.isDarkMode
                          ? "translate-x-5 bg-black"
                          : "bg-white"
                      }`}
                    />
                  </div>
                </label>
                <span className="text-sm text-white/70">Dark</span>
              </div>
            </div>

            <div>
              <SectionTitle>Sound & Haptics</SectionTitle>
              <SoundControlsInline />
            </div>
          </div>
        )}

        {activeTab === "public" && (
          <div className="space-y-6">
            <div>
              <SectionTitle>Public Map Visibility</SectionTitle>
              <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/10 mb-4">
                <div className="flex items-center gap-3">
                  {isPublished ? (
                    <Eye className="w-5 h-5 text-blue-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-white/40" />
                  )}
                  <div>
                    <p className="font-medium text-sm">
                      {isPublished ? "Visible on Mudo Map" : "Not visible on map"}
                    </p>
                    <p className="text-xs text-white/60">
                      {isPublished
                        ? "Your studio appears on the global map"
                        : "Publish to appear on the global Mudo Map"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Publish/Unpublish Buttons - Exact MudoMapWidget Pattern */}
              <div className="flex gap-2">
                {!isPublished && (
                  <button
                    type="button"
                    disabled={mapActionBusy}
                    onClick={handlePublishToMap}
                    className="flex-1 py-2 px-4 rounded-xl border border-blue-500/40 bg-blue-500/20 hover:bg-blue-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                  >
                    {mapActionBusy ? "Publishing..." : "Publish to Map"}
                  </button>
                )}
                {isPublished && (
                  <button
                    type="button"
                    disabled={mapActionBusy}
                    onClick={handleUnpublishFromMap}
                    className="flex-1 py-2 px-4 rounded-xl border border-red-500/40 bg-red-500/20 hover:bg-red-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                  >
                    {mapActionBusy ? "Updating..." : "Unpublish"}
                  </button>
                )}
              </div>
            </div>

            {isPublished && (
              <>
                <div>
                  <SectionTitle>Display Name</SectionTitle>
                  <label className="block mb-1 text-sm">
                    Public Studio Name
                  </label>
                  <input
                    type="text"
                    value={publicDraft.displayName}
                    onChange={(e) =>
                      setPublicDraft((d) => ({
                        ...d,
                        displayName: e.target.value,
                      }))
                    }
                    placeholder={draft.businessName || "My Studio"}
                    className={inputCls}
                  />
                  <p className="text-xs text-white/60 mt-1">
                    This name will appear on the map and in your public profile.
                  </p>
                </div>

                <div>
                  <SectionTitle>Location</SectionTitle>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowMapPicker(true)}
                      className="w-full px-4 py-3 rounded-lg bg-green-500/90 hover:bg-green-500 text-white font-medium text-sm transition flex items-center justify-center gap-2"
                    >
                      <span>📍</span>
                      {publicDraft.lat && publicDraft.lng ? 'Change Location on Map' : 'Pick Location on Map'}
                    </button>
                    
                    {publicDraft.lat && publicDraft.lng && (
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                        <p className="text-sm text-green-400 font-medium mb-1">
                          ✓ Location Set
                        </p>
                        <p className="text-xs text-white/60">
                          {publicDraft.lat.toFixed(6)}, {publicDraft.lng.toFixed(6)}
                        </p>
                      </div>
                    )}
                    
                    {!publicDraft.lat && !publicDraft.lng && (
                      <p className="text-xs text-white/60">
                        Click the button above to select your studio's exact location on an interactive map.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <SectionTitle>Tags</SectionTitle>
                  <p className="text-xs text-white/60 mb-3">
                    Select what services or features your studio offers:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {publicMapTags.map((tag) => (
                      <button
                        type="button"
                        key={tag}
                        onClick={() => togglePublicTag(tag)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition ${
                          publicDraft.tags.includes(tag)
                            ? "bg-blue-500/90 border-blue-500 text-white shadow"
                            : "bg-black/30 border-white/20 text-white/70 hover:bg-white/10"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <SectionTitle>Links & Contact</SectionTitle>
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1 text-sm">Website URL</label>
                      <input
                        type="url"
                        value={publicDraft.website}
                        onChange={(e) =>
                          setPublicDraft((d) => ({
                            ...d,
                            website: e.target.value,
                          }))
                        }
                        placeholder="https://mystudio.com"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm">Booking URL</label>
                      <input
                        type="url"
                        value={publicDraft.bookingUrl}
                        onChange={(e) =>
                          setPublicDraft((d) => ({
                            ...d,
                            bookingUrl: e.target.value,
                          }))
                        }
                        placeholder="https://book.mystudio.com"
                        className={inputCls}
                      />
                      <p className="text-xs text-white/60 mt-1">
                        Link to your booking page (e.g., Calendly, Acuity,
                        etc.)
                      </p>
                    </div>
                    <div>
                      <label className="block mb-1 text-sm">
                        Instagram Handle
                      </label>
                      <input
                        type="text"
                        value={publicDraft.instagram}
                        onChange={(e) =>
                          setPublicDraft((d) => ({
                            ...d,
                            instagram: e.target.value,
                          }))
                        }
                        placeholder="@mystudio"
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-sm text-blue-200 mb-2">
                    <strong>Privacy Note:</strong> Only the information above
                    will be publicly visible.
                  </p>
                  <p className="text-xs text-blue-200/80">
                    Your pricing, inventory, staff details, and other business
                    data remain private.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="sm:hidden">
            <SyncPill />
          </div>
          <div className="ml-auto flex flex-col gap-2 items-end">
            {finalizeError && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 w-full">
                {finalizeError}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-600 rounded text-white text-sm disabled:opacity-50"
                disabled={forceBlocking}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAndClose}
                className="px-4 py-2 bg-orange-500 rounded text-white text-sm disabled:opacity-70"
                title="⌘/Ctrl+Enter"
                disabled={creatingStudio}
              >
                {creatingStudio ? "Saving..." : "Save & Continue"}
              </button>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-orange-300 italic">
          Brought to you by Bark Line Studio
        </p>
      </div>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4">
          <div className="relative w-full max-w-4xl h-[600px] bg-gray-900 rounded-lg overflow-hidden border border-white/20">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-white/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Pick Your Location</h3>
                  <p className="text-sm text-white/60">Click anywhere on the map to set your studio location</p>
                </div>
                <button
                  onClick={() => setShowMapPicker(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Map */}
            <div className="w-full h-full pt-20">
              <MapContainer
                center={publicDraft.lat && publicDraft.lng ? [publicDraft.lat, publicDraft.lng] : [-33.881012, 151.167807]}
                zoom={18}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
                <MapClickHandler 
                  onLocationClick={(lat, lng) => {
                    setPublicDraft(d => ({
                      ...d,
                      lat: lat,
                      lng: lng
                    }));
                    notifyAndInbox?.({
                      notification: {
                        id: `studio-location-pin-${lat.toFixed(4)}-${lng.toFixed(4)}`,
                        title: "Studio location",
                        message: `Location set to ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                        level: "success",
                        source: "system",
                        world: "studio",
                        action: { kind: "NAVIGATE", to: "/dashboard/home" },
                      },
                    });
                    play("tap");
                    buzz("light");
                  }}
                />
                {publicDraft.lat && publicDraft.lng && (
                  <Marker position={[publicDraft.lat, publicDraft.lng]}>
                    <Popup>
                      <div className="text-sm">
                        <strong>{publicDraft.displayName || "Your Studio"}</strong><br />
                        {publicDraft.lat.toFixed(6)}, {publicDraft.lng.toFixed(6)}
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>

            {/* Info Footer */}
            {publicDraft.lat && publicDraft.lng && (
              <div className="absolute bottom-0 left-0 right-0 bg-green-500/90 backdrop-blur-sm p-3 text-center">
                <p className="text-white font-medium text-sm">
                  ✓ Location selected: {publicDraft.lat.toFixed(6)}, {publicDraft.lng.toFixed(6)}
                </p>
                <p className="text-white/80 text-xs mt-1">
                  Click "Close" and then "Save Changes" to confirm
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
function StripeStatusSync({ studio, updateStudio }) {
  const [synced, setSynced] = useState(false);
  const fetchInFlightRef = useRef(false);
  const uuidLike = /^(?:(?:[0-9a-fA-F]{8})-(?:[0-9a-fA-F]{4})-(?:[0-9a-fA-F]{4})-(?:[0-9a-fA-F]{4})-(?:[0-9a-fA-F]{12}))$/;

  useEffect(() => {
    const sid = studio?.id || studio?.studioId || null;
    if (!sid) return undefined;
    if (sid === "default") return undefined; // avoid placeholder studioId
    if (!uuidLike.test(sid)) return undefined; // only call API with real UUIDs
    if (fetchInFlightRef.current) return undefined; // guard StrictMode double-invoke

    fetchInFlightRef.current = true;
    let alive = true;
    (async () => {
      try {
        const res = await authedFetch(`/api/stripe/connect-status?studioId=${encodeURIComponent(sid)}`, {
          method: "GET",
        });
        const json = await res.json().catch(() => ({}));
        if (!alive || !json?.ok) return;
        const onboardingComplete =
          typeof json?.onboarding_complete === "boolean"
            ? json.onboarding_complete
            : !!(json?.details_submitted && json?.charges_enabled && json?.payouts_enabled);
        await updateStudio?.({
          stripe_account_id_resolved: json?.stripe_account_id_resolved || studio?.stripe_account_id_resolved || null,
          stripe_charges_enabled: !!json?.charges_enabled,
          stripe_payouts_enabled: !!json?.payouts_enabled,
          stripe_details_submitted: !!json?.details_submitted,
          stripe_onboarding_complete: onboardingComplete,
          stripe_requirements_due: json?.requirements || null,
        });
        setSynced(true);
      } catch {}
      finally {
        fetchInFlightRef.current = false;
      }
    })();
    return () => { alive = false; };
  }, [studio?.id, studio?.studioId, updateStudio]);
  return null;
}
